/**
 * @module engine3d/placement
 * @summary Where a symbol actually stands in the 3D plant, and how moving it
 *   there is written back.
 * @description
 *   The 2D drawing is a SCHEMATIC: symbols are spaced for legibility, not for
 *   the concrete they sit on. The 3D view is a physical layout. Those two want
 *   different positions, so the 3D position is stored as an OVERRIDE
 *   (`x3` / `z3` / `rot3` / `level` / `dz`) that is absent until you move
 *   something.
 *
 *   The consequence is the property that matters: dragging a machine across the
 *   3D floor cannot alter the 2D drawing. Where no override exists the item
 *   simply follows the schematic, so a drawing you never touch in 3D stays
 *   perfectly in step with itself.
 *
 *   // INVARIANT: this is the ONLY place that resolves a shape's 3D transform.
 *   Rendering, pipe routing, picking and the gizmo all go through `placeOf`, so
 *   they cannot disagree about where something is.
 */
import type { Shape } from '../types';
import { baseYOf, nearestLevel } from './levels';
import { v3, type Vec3 } from './math3';

export interface Placement {
  /** Centre of the model's footprint, world X. */
  x: number;
  /** The floor the model rests on, world Y. */
  y: number;
  /** Centre of the model's footprint, world Z. */
  z: number;
  /** Yaw in degrees, clockwise in plan (the same sense the 2D drawing uses). */
  yaw: number;
  /** True when the item has been moved in 3D and no longer tracks the 2D plan. */
  overridden: boolean;
}

/** Resolve a shape's 3D transform from its overrides, falling back to the 2D plan. */
export function placeOf(s: Shape): Placement {
  const overridden = s.x3 !== undefined || s.z3 !== undefined;
  return {
    x: s.x3 ?? s.x + s.w / 2,
    y: baseYOf(s),
    z: s.z3 ?? s.y + s.h / 2,
    yaw: s.rot3 ?? s.rotation ?? 0,
    overridden,
  };
}

/**
 * Yaw as the rotation the renderer applies about +Y.
 * The 2D drawing measures rotation clockwise on a y-down screen; +Y rotation is
 * counter-clockwise, so the sign flips. Everything that places geometry — the
 * scene builder, the port resolver, the gizmo — uses this one conversion.
 */
export function yawRadians(s: Shape): number {
  return (-(s.rot3 ?? s.rotation ?? 0) * Math.PI) / 180;
}

/** Transform a point from a model's local space into world space. */
export function toWorld(s: Shape, local: Vec3): Vec3 {
  const p = placeOf(s);
  const a = yawRadians(s);
  const c = Math.cos(a);
  const sn = Math.sin(a);
  return v3(local.x * c + local.z * sn + p.x, local.y + p.y, -local.x * sn + local.z * c + p.z);
}

/** Rotate a direction from a model's local space into world space. */
export function dirToWorld(s: Shape, local: Vec3): Vec3 {
  const a = yawRadians(s);
  const c = Math.cos(a);
  const sn = Math.sin(a);
  return v3(local.x * c + local.z * sn, local.y, -local.x * sn + local.z * c);
}

// ─── Writing a move back ─────────────────────────────────────────────────

/** Move an item across the plant floor. Writes the 3D override only. */
export function setPlan(s: Shape, x: number, z: number): void {
  s.x3 = Math.round(x * 10) / 10;
  s.z3 = Math.round(z * 10) / 10;
}

/**
 * Set an item's height.
 *
 * Snapping to the nearest floor is the default because equipment stands on
 * something; `dz` then carries whatever is left over, which is how a machine on
 * a plinth or a hopper slung under a deck gets expressed. Holding the snap off
 * keeps the exact height as an offset from the closest floor.
 */
export function setElevation(s: Shape, y: number, snap = true): void {
  const lvl = nearestLevel(y);
  if (!lvl) return;
  s.level = lvl.id;
  const off = y - lvl.elevation;
  if (snap && Math.abs(off) < 14) delete s.dz;
  else s.dz = Math.round(Math.max(-lvl.elevation, off) * 10) / 10;
}

export function setYaw(s: Shape, deg: number): void {
  s.rot3 = ((Math.round(deg) % 360) + 360) % 360;
}

/** Drop the 3D overrides so the item follows the 2D drawing again. */
export function resetPlan(s: Shape): void {
  delete s.x3;
  delete s.z3;
  delete s.rot3;
}

/** True if anything about this shape's 3D position has been overridden. */
export function hasOverride(s: Shape): boolean {
  return s.x3 !== undefined || s.z3 !== undefined || s.rot3 !== undefined || s.dz !== undefined;
}
